/**
 * @file VendorCostsPreview.tsx
 * @brief DEV ONLY: Super Admin → Billing → Service costs (Vendor costs) with
 *        the seeded `pricing_config` catalog (`/__preview/vendor-costs`).
 *
 * @details
 * Checks the vendor tabs, names, $ units, ≈ £ hints, "Not set" rows and the
 * "Save will change" list without a signed-in account. Rates below are the
 * seed values in BACKEND ensure_pricing_catalog.js, not prod's current rows.
 * Look, don't press Save: it calls the real pricing API (no token, so it
 * fails), and the local backend writes the PROD DB.
 * Mounted only when `import.meta.env.DEV` (routes/index.tsx).
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GridBlock } from "@/features/analytics/components/GridBlock";
import { ServiceCostsCard } from "../components/ServiceCostsCard";
import type { PricingConfigResponse } from "../schemas";

const SEEDED: [string, number, string][] = [
  ["whisper_asr", 0.6, "per_minute"],
  ["gpt4o_mini_transcribe", 0.3, "per_minute"],
  ["deepgram_nova", 0.43, "per_minute"],
  ["deepgram_nova_multi", 0.52, "per_minute"],
  ["deepgram_diarize", 0.2, "per_minute"],
  ["soniox_rt", 0.3, "per_minute"],
  ["gpt4mini_input", 15, "per_1m_tokens"],
  ["gpt4mini_output", 60, "per_1m_tokens"],
  ["gpt4o_input", 250, "per_1m_tokens"],
  ["gpt4o_output", 1000, "per_1m_tokens"],
  ["gpt41_input", 200, "per_1m_tokens"],
  ["gpt41_output", 800, "per_1m_tokens"],
  ["gpt41mini_input", 40, "per_1m_tokens"],
  ["gpt41mini_output", 160, "per_1m_tokens"],
  ["gpt41nano_input", 10, "per_1m_tokens"],
  ["gpt41nano_output", 40, "per_1m_tokens"],
  ["gpt4omini_tts_text", 60, "per_1m_tokens"],
  ["gpt4omini_tts_audio", 1200, "per_1m_audio_tokens"],
  ["tts1_chars", 1500, "per_1m_chars"],
  ["tts1hd_chars", 3000, "per_1m_chars"],
  ["deepgram_aura_chars", 1500, "per_1m_chars"],
  ["google_tts_chars", 3000, "per_1m_chars"],
  ["azure_tts_chars", 1500, "per_1m_chars"],
  ["soniox_tts_chars", 1400, "per_1m_chars"],
  ["emb3_small", 2, "per_1m_tokens"],
  ["emb3_large", 13, "per_1m_tokens"],
  ["gpt_realtime_translate", 3.4, "per_minute"],
  ["gpt_realtime_whisper", 1.7, "per_minute"],
];

const DATA: PricingConfigResponse = {
  success: true,
  pricing: Object.fromEntries(
    SEEDED.map(([key, cents, unit], i) => [
      key,
      {
        cost_per_unit_cents: cents,
        unit_type: unit,
        updated_at: new Date(Date.UTC(2026, 9, 19 - (i % 5), 10)).toISOString(),
      },
    ]),
  ),
};

export function VendorCostsPreviewPage() {
  const [client] = useState(
    () => new QueryClient({ defaultOptions: { mutations: { retry: false } } }),
  );
  return (
    <QueryClientProvider client={client}>
      <div className="mx-auto max-w-6xl p-4">
        <GridBlock>
          <ServiceCostsCard data={DATA} />
        </GridBlock>
      </div>
    </QueryClientProvider>
  );
}
